import Lookup from '@/components/UI/Lookup';
import { _isEmpty, _isNotBlank, _notNil } from '@/littledash';
import type { MetadataFieldWithValue } from '@/model/Metadata.model';
import Http from '@/support/http';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError.ts';
import { api as apiRoute } from '@/support/route';
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { defaultValue, renderLabel, handleChange, type SelectOption } from './Select';
import '@/components/UI/FormFields/LookupOptions.scss';
import '../MetadataFields.scss';

interface SelectLookupProps {
  field: MetadataFieldWithValue;
  isMulti?: boolean;
  testId?: string;
  name: string;
}

export const SelectLookup: FC<SelectLookupProps> = ({ field, isMulti = false, testId = 'select-lookup', name }) => {
  const [apiError, setApiError] = useState<Error | null>(null);
  const { control } = useFormContext();

  const loadOptions = async ({ inputValue, page }: { inputValue: string; page: string | number }) => {
    let url = apiRoute('metadata.options.show', { id: field.id });
    url = `${url}?page=${page}`;
    if (_isNotBlank(inputValue)) {
      url = `${url}&query=${encodeURIComponent(inputValue)}`;
    }

    try {
      const {
        data: { data, links },
      } = await Http.get(url);
      const { next } = links;
      const options = data.map(({ value }: { value: string }) => ({
        label: renderLabel(value),
        value,
      }));

      return { options, hasMore: _notNil(next) };
    } catch (error) {
      setApiError(error as Error);
      ExceptionHandler.captureException(
        new InVivoError('Failed to load metadata lookup options', {
          cause: error,
          slug: 'metadata-select-lookup',
        })
      );
      return { options: [], hasMore: false };
    }
  };

  const customGetOptionLabel = ({ label }: { label: string }) => {
    return (
      <div className="flex items-center">
        <span className="ml1 w-100 lookup-option-label">{label}</span>
      </div>
    );
  };

  return (
    <Controller
      control={control}
      defaultValue={field.value}
      name={name}
      rules={{
        validate: {
          required: (value: string | Array<string>) => {
            if (field.required === true && _isEmpty(value)) {
              return 'This field is required';
            }
            return true;
          },
        },
      }}
      render={({ field: { name: fieldName, onChange } }) => (
        <div className="lookup-select-container" data-testid={testId}>
          <Lookup
            name={fieldName}
            isMulti={isMulti}
            defaultValue={defaultValue(field, isMulti)}
            loadOptions={loadOptions}
            handleSelect={(data: SelectOption | Array<SelectOption> | null) => handleChange(onChange, data, isMulti)}
            cacheOptions={false}
            getOptionLabel={customGetOptionLabel}
          />
          {_notNil(apiError) && <p className="f7 red">There was a problem fetching metadata</p>}
        </div>
      )}
    />
  );
};
